import { z, GuidelineSchema } from '@guestguru/shared';
import { apiGet, apiPost, apiPut, apiDelete, planningPath } from './PlanningApiClient';

type ApiGuideline = z.infer<typeof GuidelineSchema> & {
  items?: GuidelineItem[];
  vendor_ids?: string[];
  team_tag?: string;
};

export interface GuidelineItem {
  id: string;
  text: string;
  emphasis?: boolean;
}

export interface Guideline {
  id: string;
  title: string;
  category: GuidelineCategory;
  items: GuidelineItem[];
  archived?: boolean;
  vendor_ids?: string[];
  team_tag?: string;
  created_at: string;
  updated_at: string;
}

export type GuidelineCategory =
  | 'music'
  | 'photography'
  | 'catering'
  | 'security'
  | 'check-in'
  | 'decoration'
  | 'logistics'
  | 'bridal-party'
  | 'grooms-party'
  | 'vendors'
  | 'guests'
  | 'ceremony'
  | 'reception'
  | 'after-party'
  | 'general';

export const GUIDELINE_CATEGORY_LABELS: Record<GuidelineCategory, string> = {
  'music': 'Music',
  'photography': 'Photography',
  'catering': 'Catering',
  'security': 'Security',
  'check-in': 'Check-in',
  'decoration': 'Decoration',
  'logistics': 'Logistics',
  'bridal-party': 'Bridal Party',
  'grooms-party': "Groom's Party",
  'vendors': 'Vendors',
  'guests': 'Guests',
  'ceremony': 'Ceremony',
  'reception': 'Reception',
  'after-party': 'After Party',
  'general': 'General',
};

export const GUIDELINE_CATEGORIES = Object.keys(GUIDELINE_CATEGORY_LABELS) as GuidelineCategory[];

// ── In-memory cache (no localStorage for app data) ──
let guidelinesCache: Guideline[] = [];

// ── Read from in-memory cache ──

export const loadGuidelines = (): Guideline[] => {
  return guidelinesCache;
};

export const saveGuidelines = (guidelines: Guideline[]): void => {
  guidelinesCache = guidelines;
};

// ── API sync ──

/**
 * Pull guidelines from the Planning API into the in-memory cache.
 * Call on component mount to hydrate with latest data.
 */
export async function refreshGuidelinesFromApi(): Promise<Guideline[]> {
  try {
    const response = await apiGet<{ guidelines: ApiGuideline[] }>(planningPath('/guidelines'));
    if (Array.isArray(response?.guidelines) && response.guidelines.length > 0) {
      const normalised: Guideline[] = response.guidelines.map(row => ({
        id: String(row.id),
        title: row.title,
        category: (row.category ?? 'general') as GuidelineCategory,
        items: Array.isArray(row.items) ? row.items : [],
        archived: row.archived ?? false,
        vendor_ids: Array.isArray(row.vendor_ids) ? row.vendor_ids : [],
        team_tag: row.team_tag,
        created_at: row.created_at,
        updated_at: row.updated_at,
      }));
      saveGuidelines(normalised);
      return normalised;
    }
  } catch {
    // API unreachable — keep existing in-memory cache
  }
  return loadGuidelines();
}

// ── Convenience reads ──

export const getGuidelineById = (id: string): Guideline | null => {
  const guidelines = loadGuidelines();
  return guidelines.find(g => g.id === id) || null;
};

export const getGuidelinesByCategory = (category: GuidelineCategory): Guideline[] => {
  const guidelines = loadGuidelines();
  return guidelines.filter(g => g.category === category && !g.archived);
};

// ── Writes — in-memory first, then fire-and-forget to API ──

/**
 * Creates a guideline. By default an unreachable API keeps the local copy under
 * a temporary id. With `{ strict: true }` a failed POST removes that copy and
 * rethrows the ApiError instead, so a form can show the 422's field errors and
 * stay open rather than reporting a save that never happened.
 */
export const createGuideline = async (
  data: Omit<Guideline, 'id' | 'created_at' | 'updated_at'>,
  opts: { strict?: boolean } = {},
): Promise<Guideline> => {
  const guidelines = loadGuidelines();
  const now = new Date().toISOString();
  const tempId = `gl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const tempGuideline: Guideline = { ...data, id: tempId, created_at: now, updated_at: now };
  saveGuidelines([...guidelines, tempGuideline]);

  try {
    const res = await apiPost<{ guideline?: { id: number | string }; id?: number | string }>(planningPath('/guidelines'), {
      title: tempGuideline.title,
      category: tempGuideline.category,
      items: tempGuideline.items,
      archived: tempGuideline.archived ?? false,
      vendor_ids: tempGuideline.vendor_ids ?? [],
      team_tag: tempGuideline.team_tag ?? null,
    });
    const dbId = String(res?.guideline?.id ?? res?.id ?? '');
    if (dbId && dbId !== tempId) {
      const withDbId: Guideline = { ...tempGuideline, id: dbId };
      saveGuidelines([...loadGuidelines().filter(g => g.id !== tempId), withDbId]);
      return withDbId;
    }
  } catch (err) {
    if (opts.strict) {
      saveGuidelines(loadGuidelines().filter(g => g.id !== tempId));
      throw err;
    }
    /* API unreachable — keep temp ID */
  }

  return tempGuideline;
};

export const updateGuideline = (id: string, updates: Partial<Omit<Guideline, 'id' | 'created_at'>>): Guideline | null => {
  const guidelines = loadGuidelines();
  const index = guidelines.findIndex(g => g.id === id);
  if (index === -1) return null;

  const updated: Guideline = {
    ...guidelines[index],
    ...updates,
    updated_at: new Date().toISOString(),
  };
  const newList = [...guidelines];
  newList[index] = updated;
  saveGuidelines(newList);

  // Fire-and-forget PUT to API
  apiPut(planningPath(`/guidelines/${id}`), {
    title: updated.title,
    category: updated.category,
    items: updated.items,
    archived: updated.archived ?? false,
    vendor_ids: updated.vendor_ids ?? [],
    team_tag: updated.team_tag ?? null,
  }).catch(() => { /* API unreachable */ });

  return updated;
};

/**
 * Updates a guideline and waits for the API. Unlike `updateGuideline` (local
 * first, PUT in the background) a failed PUT puts the old copy back and
 * rethrows the ApiError so the form can show its field errors.
 */
export const updateGuidelineStrict = async (
  id: string,
  updates: Partial<Omit<Guideline, 'id' | 'created_at'>>,
): Promise<Guideline | null> => {
  const before = loadGuidelines();
  const existing = before.find(g => g.id === id);
  if (!existing) return null;
  const updated: Guideline = { ...existing, ...updates, updated_at: new Date().toISOString() };
  saveGuidelines(before.map(g => (g.id === id ? updated : g)));
  try {
    await apiPut(planningPath(`/guidelines/${id}`), {
      title: updated.title,
      category: updated.category,
      items: updated.items,
      archived: updated.archived ?? false,
      vendor_ids: updated.vendor_ids ?? [],
      team_tag: updated.team_tag ?? null,
    });
  } catch (err) {
    saveGuidelines(before);
    throw err;
  }
  return updated;
};

/** Soft-delete: mark as archived */
export const archiveGuideline = (id: string): Guideline | null => {
  return updateGuideline(id, { archived: true });
};

/** Restore an archived guideline back to active */
export const restoreGuideline = (id: string): Guideline | null => {
  return updateGuideline(id, { archived: false });
};

/** Permanent delete — only allowed for already-archived items */
export const deleteGuideline = (id: string): boolean => {
  const guidelines = loadGuidelines();
  const target = guidelines.find(g => g.id === id);
  if (!target || !target.archived) return false;
  const filtered = guidelines.filter(g => g.id !== id);
  saveGuidelines(filtered);

  // Fire-and-forget DELETE to API
  apiDelete(planningPath(`/guidelines/${id}`)).catch(() => { /* API unreachable */ });

  return true;
};
